import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { SocketContext } from '../../contexts/SocketContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getMessages, sendMessage, markAsRead } from '../../services/chatService';

const ChatBox = ({ studentId, teamMembers, chatInputRef }) => {
  const { socket } = useContext(SocketContext);
  const { mongoUser } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Typing indicator state
  const [typingUser, setTypingUser] = useState(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingEmitRef = useRef(0);

  // Scroll to bottom — instant on first load, smooth on new messages
  const scrollToBottom = useCallback((instant = false) => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: instant ? 'instant' : 'smooth',
      });
    }
  }, []);

  // Fetch initial messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        isInitialLoad.current = true;
        const { data } = await getMessages(studentId, 1);
        const fetched = data.data?.messages || [];
        setMessages(fetched);
        setHasMore(fetched.length >= 50);
        setPage(1);
      } catch (err) {
        console.error('Failed to fetch messages:', err);
      } finally {
        setLoading(false);
      }
    };

    if (studentId) {
      fetchMessages();
    }
  }, [studentId]);

  // Mark messages as read on mount
  useEffect(() => {
    if (studentId) {
      markAsRead(studentId).catch((err) =>
        console.error('Failed to mark messages as read:', err)
      );
    }
  }, [studentId]);

  // Scroll to bottom after initial messages load
  useEffect(() => {
    if (!loading && messages.length > 0 && isInitialLoad.current) {
      isInitialLoad.current = false;
      requestAnimationFrame(() => scrollToBottom(true));
    }
  }, [loading, messages.length, scrollToBottom]);

  // Join/leave socket room
  useEffect(() => {
    if (socket && studentId) {
      socket.emit('join_chat', studentId);

      return () => {
        socket.emit('leave_chat', studentId);
      };
    }
  }, [socket, studentId]);

  // Listen for new real-time messages
  useEffect(() => {
    if (socket) {
      const handleNewMessage = (message) => {
        setMessages((prev) => [...prev, message]);
        requestAnimationFrame(() => scrollToBottom(false));
        // Clear typing indicator when message arrives
        setTypingUser(null);
        if (message.senderId !== mongoUser?._id) {
          markAsRead(studentId).catch(() => {});
        }
      };

      socket.on('new_message', handleNewMessage);

      return () => {
        socket.off('new_message', handleNewMessage);
      };
    }
  }, [socket, studentId, mongoUser, scrollToBottom]);

  // Listen for typing indicators
  useEffect(() => {
    if (!socket) return;

    const handleUserTyping = ({ userId, userName, isTyping }) => {
      if (userId === mongoUser?._id) return;

      if (isTyping) {
        setTypingUser(userName);
        // Auto-clear after 3 seconds
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
      } else {
        setTypingUser(null);
      }
    };

    socket.on('user_typing', handleUserTyping);
    return () => {
      socket.off('user_typing', handleUserTyping);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [socket, mongoUser]);

  // Emit typing event (debounced, 2s cooldown)
  const handleTyping = useCallback(() => {
    if (!socket || !studentId) return;
    const now = Date.now();
    if (now - lastTypingEmitRef.current < 2000) return;
    lastTypingEmitRef.current = now;
    socket.emit('typing', { studentId, isTyping: true });
  }, [socket, studentId]);

  // Load older messages on scroll to top
  const handleScroll = async () => {
    if (!containerRef.current || !hasMore || loadingMore) return;
    if (containerRef.current.scrollTop < 40) {
      try {
        setLoadingMore(true);
        const prevScrollHeight = containerRef.current.scrollHeight;
        const nextPage = page + 1;
        const { data } = await getMessages(studentId, nextPage);
        const fetched = data.data?.messages || [];
        if (fetched.length > 0) {
          setMessages((prev) => [...fetched, ...prev]);
          setPage(nextPage);
          setHasMore(fetched.length >= 50);
          requestAnimationFrame(() => {
            if (containerRef.current) {
              const newScrollHeight = containerRef.current.scrollHeight;
              containerRef.current.scrollTop = newScrollHeight - prevScrollHeight;
            }
          });
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error('Failed to load more messages:', err);
      } finally {
        setLoadingMore(false);
      }
    }
  };

  const handleSend = async (content, attachments) => {
    try {
      // Stop typing indicator when sending
      if (socket && studentId) {
        socket.emit('typing', { studentId, isTyping: false });
      }
      const { data } = await sendMessage(studentId, content, attachments);
      const newMsg = data.data?.message;
      if (newMsg) {
        setMessages((prev) => {
          const exists = prev.some((m) => m._id === newMsg._id);
          return exists ? prev : [...prev, newMsg];
        });
        requestAnimationFrame(() => scrollToBottom(false));
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Messages area */}
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1.5,
          backgroundColor: '#F8FAFC',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            width: 5,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(15,23,42,0.12)',
            borderRadius: 3,
            '&:hover': {
              backgroundColor: 'rgba(15,23,42,0.2)',
            },
          },
        }}
      >
        {/* Loading older messages spinner */}
        {loadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} sx={{ color: '#1A73E8' }} />
          </Box>
        )}

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <CircularProgress size={28} sx={{ color: '#1A73E8' }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography variant="body2" sx={{ color: '#5F6368' }}>
              No messages yet. Start the conversation.
            </Typography>
          </Box>
        ) : (
          messages.map((msg) => (
            <ChatMessage
              key={msg._id}
              message={msg}
              isOwn={msg.senderId === mongoUser?._id}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Typing indicator */}
      {typingUser && (
        <Box
          sx={{
            px: 2,
            py: 0.5,
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '3px',
              alignItems: 'center',
              '@keyframes typingDot': {
                '0%, 60%, 100%': { opacity: 0.3, transform: 'translateY(0)' },
                '30%': { opacity: 1, transform: 'translateY(-2px)' },
              },
            }}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  backgroundColor: '#1A73E8',
                  animation: 'typingDot 1.2s infinite',
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: '#5F6368', fontSize: '0.72rem' }}>
            {typingUser} is typing...
          </Typography>
        </Box>
      )}

      {/* Input area */}
      <ChatInput onSend={handleSend} onTyping={handleTyping} teamMembers={teamMembers} ref={chatInputRef} />
    </Paper>
  );
};

export default ChatBox;
